import { create } from "zustand";

import { logger } from "@/telemetry";

/**
 * Tool Preferences Store
 *
 * Manages per-user tool selection preferences with localStorage persistence.
 *
 * A preference is stored as a **difference from the shared workflow definition**,
 * never as a snapshot of the whole tool list (LSEG-190). The earlier shape was
 * `{ workflowId: { agentId: string[] } }` and `applyToolPreferences` substituted
 * that list wholesale, so a connector added to the definition after a user's
 * snapshot was written never reached that user again. Recording only what the
 * user switched on beyond the definition and what they switched off leaves a
 * newly added tool in neither set, so it flows through automatically.
 */

const STORAGE_KEY = "tool-preferences";

/**
 * Persisted shape version. Version 1 was the pre-delta snapshot and had no
 * marker at all, so an unversioned blob is refused on read in both the
 * localStorage and the roaming profile paths. See ADR-0026.
 */
export const PREFERENCES_VERSION = 2;

/**
 * Reserved workflow key carrying the shape version.
 *
 * The user profile transports this map as `config.toolPreferences`, which the
 * admin SDK declares as `Record<string, Record<string, string[]>>` and validates
 * before any HTTP happens, and which the admin service models as
 * `Dict[str, Dict[str, List[str]]]`. A `{version, preferences}` envelope is not
 * that shape and is refused client-side, so the version marker lives inside the
 * map instead, as a single reserved workflow key whose value is
 * `{"v": ["2"]}` — one non-empty top-level key, itself a `Record<string,
 * string[]>`. No workflow id may be recorded under it. See ADR-0026.
 */
export const VERSION_KEY = "__version";

/** The field inside the version marker that carries the version number. */
const VERSION_FIELD = "v";

/** One agent's tool selection, expressed against the shared definition. */
export type AgentToolPreference = {
  /** Tool ids the user switched on that the shared definition does not carry. */
  enabled: string[];
  /** Tool ids from the shared definition the user switched off. */
  disabled: string[];
};

export type ToolPreferences = Record<string, Record<string, AgentToolPreference>>;

/**
 * The map written to localStorage and to the user profile: the preferences
 * themselves plus the reserved `__version` key. Declared as the wire type the
 * admin SDK accepts rather than as `ToolPreferences`, because it is what
 * crosses the network. `AgentToolPreference` is a type alias precisely so that
 * it carries an implicit index signature and assigns to `string[]` records
 * without a cast.
 */
export type StoredToolPreferences = Record<string, Record<string, string[]>>;

export interface ToolPreferencesState {
  /** Every recorded delta, keyed by workflow and then by agent. */
  preferences: ToolPreferences;

  /**
   * Record one tool's on/off decision for a workflow agent, leaving every other
   * tool's recorded state alone. Tool-scoped rather than list-scoped because
   * every caller is a single switch, and because diffing a whole list caught
   * unrelated tools that other effects had just added.
   *
   * @param isInDefinition whether the shared definition carries this tool
   */
  setAgentToolPreference: (
    workflowId: string,
    agentId: string,
    toolId: string,
    isSelected: boolean,
    isInDefinition: boolean,
  ) => void;

  /** Get the stored delta for a workflow agent, or undefined if none is recorded. */
  getAgentPreference: (workflowId: string, agentId: string) => AgentToolPreference | undefined;

  /** Check whether any preferences exist for a given workflow. */
  hasPreferences: (workflowId: string) => boolean;

  /** Remove all stored preferences for a workflow. */
  clearWorkflowPreferences: (workflowId: string) => void;

  /**
   * Merge preferences roamed through the user profile into the store.
   * localStorage values win on conflict. An unversioned payload is pre-delta
   * and is ignored rather than migrated.
   */
  loadFromBackend: (payload: unknown) => void;

  /** Return the version-marked map for backend sync. */
  getPreferencesForSync: () => StoredToolPreferences;
}

/** Type guard for one agent's delta. */
function isValidAgentPreference(value: unknown): value is AgentToolPreference {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const { enabled, disabled } = value as Record<string, unknown>;

  if (!Array.isArray(enabled) || !Array.isArray(disabled)) return false;

  return (
    enabled.every((t) => typeof t === "string") && disabled.every((t) => typeof t === "string")
  );
}

/**
 * Keep the entries of a payload that conform to the delta shape and drop the
 * rest, rather than refusing the whole payload for one bad entry.
 *
 * Empty-string workflow and agent keys are dropped here (PWI-286): an empty key
 * syncs verbatim to the backend and produces a DynamoDB item with an empty
 * attribute name, which the write rejects. Dropping only the offending entry is
 * what lets a user whose profile already carries one recover the preferences
 * either side of it instead of losing the lot. The reserved version key is
 * dropped for the same reason it is written: it is bookkeeping, not a workflow.
 *
 * Exported for direct unit testing.
 */
export function sanitiseToolPreferences(value: unknown): ToolPreferences {
  const sanitised: ToolPreferences = {};

  if (!value || typeof value !== "object" || Array.isArray(value)) return sanitised;

  for (const [workflowId, agents] of Object.entries(value as Record<string, unknown>)) {
    if (!workflowId || workflowId === VERSION_KEY) continue;
    if (!agents || typeof agents !== "object" || Array.isArray(agents)) continue;

    const kept: Record<string, AgentToolPreference> = {};

    for (const [agentId, preference] of Object.entries(agents as Record<string, unknown>)) {
      if (!agentId) continue;
      if (!isValidAgentPreference(preference)) continue;

      kept[agentId] = { enabled: [...preference.enabled], disabled: [...preference.disabled] };
    }

    if (Object.keys(kept).length > 0) sanitised[workflowId] = kept;
  }

  return sanitised;
}

/** Whether a payload carries the reserved marker for the current shape version. */
function hasCurrentVersionMarker(payload: Record<string, unknown>): boolean {
  const marker = payload[VERSION_KEY];

  if (!marker || typeof marker !== "object" || Array.isArray(marker)) return false;

  const version = (marker as Record<string, unknown>)[VERSION_FIELD];

  return Array.isArray(version) && version[0] === String(PREFERENCES_VERSION);
}

/**
 * Read a stored map, from localStorage or from the user profile.
 *
 * Returns null for anything that does not carry the current version marker; the
 * pre-delta snapshot has no marker at all, so this is what discards it and lets
 * an affected user recover on their next page load. A marked payload is
 * sanitised rather than refused, so one corrupt entry costs only itself.
 * Exported for testing.
 */
export function parseStoredPreferences(payload: unknown): ToolPreferences | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  if (!hasCurrentVersionMarker(payload as Record<string, unknown>)) return null;

  return sanitiseToolPreferences(payload);
}

/** Stamp the version marker onto a delta map, giving the shape that is persisted. */
export function toStoredPreferences(preferences: ToolPreferences): StoredToolPreferences {
  // The marker is spread last so a workflow that somehow reached the store under
  // the reserved key can never shadow it.
  return {
    ...preferences,
    [VERSION_KEY]: { [VERSION_FIELD]: [String(PREFERENCES_VERSION)] },
  };
}

/**
 * Resolve an agent's effective tool list by layering a recorded delta over the
 * shared definition. Definition order is preserved and extras are appended, so
 * a tool the definition gains later is present without any migration.
 */
export function applyAgentToolPreference(
  baseTools: string[],
  preference: AgentToolPreference | undefined,
): string[] {
  if (!preference) return baseTools;

  const kept = baseTools.filter((tool) => !preference.disabled.includes(tool));
  const extras = preference.enabled.filter((tool) => !kept.includes(tool));

  return [...kept, ...extras];
}

function readFromLocalStorage(): ToolPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored) {
      return parseStoredPreferences(JSON.parse(stored)) ?? {};
    }
  } catch {
    // Corrupted or unavailable localStorage — start fresh
  }

  return {};
}

function writeToLocalStorage(preferences: ToolPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStoredPreferences(preferences)));
  } catch (error) {
    logger.warn("Failed to save tool preferences to localStorage", {
      error: String(error),
    });
  }
}

/**
 * Merge one agent's roamed delta with the local one, deciding tool by tool.
 *
 * A delta is a set of per-tool decisions, so the merge must be per tool as
 * well: local wins for any tool it has recorded a decision on, and the backend
 * fills in the tools local has never decided. Replacing the whole agent object
 * with local's copy silently discarded an opt-in made on another device for a
 * tool this device had no opinion about.
 */
function mergeAgentPreference(
  backend: AgentToolPreference,
  local: AgentToolPreference,
): AgentToolPreference {
  const locallyDecided = new Set([...local.enabled, ...local.disabled]);

  return {
    enabled: [...local.enabled, ...backend.enabled.filter((tool) => !locallyDecided.has(tool))],
    disabled: [...local.disabled, ...backend.disabled.filter((tool) => !locallyDecided.has(tool))],
  };
}

/** Add or remove one id, returning a new array only where the membership changes. */
function withMembership(list: string[], toolId: string, shouldContain: boolean): string[] {
  const contains = list.includes(toolId);

  if (contains === shouldContain) return list;

  return shouldContain ? [...list, toolId] : list.filter((tool) => tool !== toolId);
}

export const useToolPreferencesStore = create<ToolPreferencesState>((set, get) => ({
  preferences: readFromLocalStorage(),

  setAgentToolPreference: (
    workflowId: string,
    agentId: string,
    toolId: string,
    isSelected: boolean,
    isInDefinition: boolean,
  ) => {
    // Never record an empty key (PWI-286): an empty workflowId or agentId syncs
    // verbatim to the backend and produces a DynamoDB item with an empty
    // attribute name, which the write rejects. The reserved version key is
    // refused for the same reason: it must stay bookkeeping, not a workflow.
    if (!workflowId || !agentId || !toolId) return;
    if (workflowId === VERSION_KEY) return;

    const current = get().preferences;
    const existing = current[workflowId]?.[agentId] ?? { enabled: [], disabled: [] };

    // A selection that already matches the definition needs no record at all,
    // which is what lets a delta prune itself back to nothing.
    const next: AgentToolPreference = {
      enabled: withMembership(existing.enabled, toolId, isSelected && !isInDefinition),
      disabled: withMembership(existing.disabled, toolId, !isSelected && isInDefinition),
    };

    const agents = { ...current[workflowId] };

    if (next.enabled.length === 0 && next.disabled.length === 0) {
      delete agents[agentId];
    } else {
      agents[agentId] = next;
    }

    const updated: ToolPreferences = { ...current };

    if (Object.keys(agents).length === 0) {
      delete updated[workflowId];
    } else {
      updated[workflowId] = agents;
    }

    set({ preferences: updated });
    writeToLocalStorage(updated);
  },

  getAgentPreference: (workflowId: string, agentId: string) => {
    return get().preferences[workflowId]?.[agentId];
  },

  hasPreferences: (workflowId: string) => {
    return workflowId in get().preferences;
  },

  clearWorkflowPreferences: (workflowId: string) => {
    const current = get().preferences;
    const { [workflowId]: _removed, ...remaining } = current;

    set({ preferences: remaining });
    writeToLocalStorage(remaining);
  },

  loadFromBackend: (payload: unknown) => {
    const backendPrefs = parseStoredPreferences(payload);

    if (!backendPrefs) {
      logger.info("Ignoring tool preferences written in an unsupported shape");

      return;
    }

    const local = get().preferences;

    // `parseStoredPreferences` has already dropped the empty and reserved keys a
    // corrupted profile may carry (PWI-286), so both sources are trustworthy by
    // the time they are merged here. The merge is per tool: localStorage has the
    // last word on any tool it has recorded a decision on, being the more recent
    // of the two, while the backend fills whatever this device has never seen,
    // down to individual tools inside an agent both sides know about.
    const merged: ToolPreferences = {};

    for (const [workflowId, agents] of Object.entries(backendPrefs)) {
      merged[workflowId] = { ...agents };
    }

    for (const [workflowId, agents] of Object.entries(local)) {
      const target = { ...merged[workflowId] };

      for (const [agentId, preference] of Object.entries(agents)) {
        const roamed = target[agentId];

        target[agentId] = roamed ? mergeAgentPreference(roamed, preference) : preference;
      }

      merged[workflowId] = target;
    }

    set({ preferences: merged });
    writeToLocalStorage(merged);
  },

  getPreferencesForSync: () => {
    return toStoredPreferences(get().preferences);
  },
}));
